import { z } from 'zod'

/** Legacy phonebook `withIntlPhoneNumber()` → `max_digits:14`. */
export const DIAL_STRING_PHONE_MAX_DIGITS = 14

/** Legacy call-flow forward `maxlength="24"`. */
export const DIAL_STRING_CALL_FLOW_MAX_LENGTH = 24

export const DIAL_STRING_PHONE_MAX_DIGITS_MESSAGE =
  'The phone number field must not have more than 14 digits.'

export const DIAL_STRING_PHONE_HELPER = 'May include only digits, commas, and asterisks'

export const DIAL_STRING_PHONE_PLACEHOLDER = 'e.g. 5551234567 or *101,3'

export const DIAL_STRING_CALL_FLOW_PLACEHOLDER = 'e.g. 18005551234 or *101,3'

/** Plain E.164-style entry or star code with optional comma-separated pauses (e.g. `*101,3`). */
export const DIAL_STRING_PHONE_VALID_REGEX = /^(?:[0-9]{1,14}|\*[0-9]+(?:,[0-9]+)*)$/

export type DialStringPhoneMode = 'phonebook' | 'callflow'

export type SanitizeDialStringPhoneOptions = {
  mode?: DialStringPhoneMode
  /** When true (submit/validation), strip a trailing comma. Keep false while typing. */
  trimTrailingComma?: boolean
}

export function countDialStringDigits(value: string): number {
  return (value.match(/\d/g) ?? []).length
}

/**
 * Normalize user input: strip invalid chars; drop commas unless `*` present;
 * collapse/remove empty comma runs; cap digits / length per mode.
 */
export function sanitizeDialStringPhone(
  value: string,
  options: SanitizeDialStringPhoneOptions = {}
): string {
  const mode = options.mode ?? 'phonebook'
  const trimTrailingComma = options.trimTrailingComma ?? false
  let s = value.replace(/[^0-9,*]/g, '')

  if (!s.includes('*')) {
    const digits = s.replace(/\D/g, '')
    const maxDigits =
      mode === 'callflow' ? DIAL_STRING_CALL_FLOW_MAX_LENGTH : DIAL_STRING_PHONE_MAX_DIGITS
    return digits.slice(0, maxDigits)
  }

  const star = s.indexOf('*')
  if (star > 0) {
    s = s.slice(star)
  }

  s = s.replace(/,+/g, ',').replace(/^,/, '')
  if (trimTrailingComma) {
    s = s.replace(/,$/, '')
  }

  if (mode === 'callflow') {
    return s.slice(0, DIAL_STRING_CALL_FLOW_MAX_LENGTH)
  }

  let out = ''
  let digitCount = 0
  for (const char of s) {
    if (/\d/.test(char)) {
      if (digitCount >= DIAL_STRING_PHONE_MAX_DIGITS) continue
      digitCount++
    }
    out += char
  }

  return out
}

export function isValidDialStringPhone(
  value: string,
  mode: DialStringPhoneMode = 'phonebook'
): boolean {
  const sanitized = sanitizeDialStringPhone(value, { mode, trimTrailingComma: true })
  if (!sanitized) return false

  if (mode === 'callflow') {
    if (sanitized.length > DIAL_STRING_CALL_FLOW_MAX_LENGTH) return false
    if (sanitized.includes(',') && !sanitized.startsWith('*')) return false
    if (/,{2,}/.test(sanitized) || sanitized.startsWith(',')) return false
    return /^[0-9,*]+$/.test(sanitized)
  }

  return DIAL_STRING_PHONE_VALID_REGEX.test(sanitized)
}

export function isDialStringPhoneKeyAllowed(
  key: string,
  currentValue: string,
  mode: DialStringPhoneMode = 'phonebook'
): boolean {
  if (key === 'Enter') return true
  if (!/^[\d,*]$/.test(key)) return false

  if (key === ',') {
    if (!currentValue.length || currentValue.endsWith(',') || currentValue.endsWith('*')) {
      return false
    }
    if (mode === 'callflow') {
      return true
    }
    // Phonebook: comma only in star codes (e.g. *101,3) — need * then digits before comma
    return /^\*\d+$/.test(currentValue)
  }

  if (key === '*') {
    return currentValue.length === 0
  }

  return true
}

export const dialStringPhoneSchema = z
  .string()
  .min(1, 'Phone number is required')
  .refine((value) => !value.includes(',') || value.includes('*'), {
    message: DIAL_STRING_PHONE_HELPER
  })
  .refine((value) => isValidDialStringPhone(value, 'phonebook'), {
    message: DIAL_STRING_PHONE_HELPER
  })

export const optionalDialStringPhoneSchema = z
  .string()
  .optional()
  .nullable()
  .refine((value) => !value || value === '' || isValidDialStringPhone(value, 'phonebook'), {
    message: DIAL_STRING_PHONE_HELPER
  })
